import { z } from "zod";
import type { Paginated } from "../envelope";
import { keysetQuery, paginationQuery, searchParam } from "../pagination";
import {
	type CompetitorMemberRef,
	type CompetitorRef,
	type NocRef,
	competitionDayRangeShape,
	competitionFromBeforeTo,
	competitionSplitList,
	versionField,
} from "./common";
import { EVENT_GENDERS, type EventGender, MEDALS, type Medal } from "./vocab";

/**
 * Medals — OVR's ranking proposal, the official podium (the medal desk,
 * MSES), the tally, the medal list, the desk's two review lists and the event
 * lock:
 *
 *   PUT  /events/:id/ranking   OVR's final ranking and suggested medals — a PROPOSAL
 *   GET  /events/:id/medals    the podium, the proposal beside it, warnings
 *   PUT  /events/:id/medals    set the whole podium: THE award (MSES only)
 *   PUT  /events/:id/medal-ceremony   the ceremony time (MSES only)
 *   POST /events/:id/lock      freeze everything beneath the event (MSES only)
 *   POST /events/:id/unlock    … and thaw it, with a reason (MSES only)
 *   GET  /medals/tally         medals per organization in one games
 *   GET  /medals               the awarded medals, newest first
 *   GET  /medals/pending       decided medal events the desk has not settled
 *   GET  /medals/changed       awarded events whose result or proposal moved since
 *   GET  /medals/events        every medal event with its state — the medal board (desk + admins)
 *   GET  /medals/daily         per games day: events due / settled, medals due / awarded, firsts
 *   GET  /events/:id/medals/history   the podium's audit trail (desk + admins)
 *
 * "MSES only" = `MEDAL_AWARD_ROLES` (the MEDAL_DESK role), which OVR refuses
 * to sign in with: OVR suggests, MSES decides (see `MEDAL_AWARD_ROLES`).
 *
 * ── A medal belongs to the EVENT, and a row is the award ────────────────────
 * The predecessor hung a medal on a MEET as a frozen copy of a participant
 * row. 265 of its 794 events decided their podium across two or three meets,
 * so every read rebuilt the podium by guessing from stage names, and 327
 * copies drifted from the live result. It had no unique key either: one meet
 * was approved twice and the tally read 81 instead of 80. Here a medal is an
 * `event_medals` row — (event, competitor), unique — and a row existing IS
 * the official award. Only that table is ever counted or shown as a medal.
 *
 * `final_rank` / `proposed_medal` (`PUT /events/:id/ranking`) are OVR's
 * guideline ("John gold, Bob silver, Brandon bronze"). They are stored and
 * shown to the desk — the predecessor's backend silently dropped them — and
 * never counted.
 *
 * Every export is prefixed (medal…, eventRanking…, eventPodium…, eventLock…):
 * all competition modules are re-exported through one barrel, and two modules
 * exporting the same name is a compile error there.
 */

// ─── Limits ──────────────────────────────────────────────────────────────────
//
// One copy, read by the API's validators and by any form (CLAUDE.md, bug #4).

/** Competitors one `PUT /events/:id/ranking` may rank — a marathon field fits. */
export const EVENT_RANKING_MAX = 500;
/** Medals one podium may hold: a tie for every colour with two bronzes is far below this. */
export const EVENT_PODIUM_MAX = 12;
export const MEDAL_NOTE_MAX = 500;
/** The audit headline for a change. Same ceiling as the API's own `reasonField` (pinned by the parity test). */
export const MEDAL_REASON_MAX = 300;
/**
 * Rows of OVR's proposal shown beside a podium (`GET /events/:id/medals`,
 * `GET /medals/pending`): every competitor OVR proposed a medal for, then the
 * best-ranked others, this many in all.
 */
export const MEDAL_PROPOSAL_MAX = 16;
/** `final_rank` is a `smallint` column: a larger number is a 422 here, not a Postgres error. */
export const EVENT_FINAL_RANK_MAX = 32_767;

// ─── The medal board ─────────────────────────────────────────────────────────

/**
 * Where a medal event stands — ONE of these, decided in SQL by one builder
 * (`medalEventStatesSql`, apps/api) behind `/medals/progress`, `/medals/events`
 * and `/medals/daily`, so a chip, a pipeline node and a list never disagree.
 * In pipeline order (the flow MSES draws), first rule that applies wins:
 *
 *   AWARDED        it has an `event_medals` row
 *   WAITING        medal meets, every one OFFICIAL, no desk call since the
 *                  last became official (`/medals/pending`'s rule)
 *   NO_MEDALS      a desk call on record otherwise (`/medals/no-medals`'s rule)
 *   COMPETING      medal meets, not all OFFICIAL yet
 *   NOT_SCHEDULED  no medal meet yet
 *
 * The array is the PIPELINE order (not the rule order): not scheduled →
 * competing → waiting → awarded, with no medals the branch off waiting.
 */
export const MEDAL_EVENT_STATES = ["NOT_SCHEDULED", "COMPETING", "WAITING", "AWARDED", "NO_MEDALS"] as const;
export type MedalEventState = (typeof MEDAL_EVENT_STATES)[number];

/**
 * `GET /medals/events` orders: `schedule` = by the deciding meet's start
 * (`due_at`, unscheduled last); `recent` = latest activity first (the last
 * award, else the desk's last call, else when the event was decided).
 */
export const MEDAL_EVENT_SORTS = ["schedule", "recent"] as const;
export type MedalEventSort = (typeof MEDAL_EVENT_SORTS)[number];

/** `GET /medals/tally?breakdown=` — a cell per sport, or per games day (the day the medal was awarded). */
export const MEDAL_TALLY_BREAKDOWNS = ["sport", "day"] as const;
export type MedalTallyBreakdown = (typeof MEDAL_TALLY_BREAKDOWNS)[number];

/** The review lists' orders: `queue` = the list's own order; `sport` = grouped by sport (programme order), the list's order inside. */
export const MEDAL_REVIEW_ORDERS = ["queue", "sport"] as const;
export type MedalReviewOrder = (typeof MEDAL_REVIEW_ORDERS)[number];

/**
 * The desk's target: an event is awarded within this many minutes of its last
 * medal meet becoming official. MSES shows "Award by 14:35" against it — an
 * absolute time, never a countdown.
 */
export const MEDAL_AWARD_TARGET_MINUTES = 30;

/**
 * Days `GET /medals/daily` answers at most: the games' calendar (a SEA Games
 * runs under three weeks, with competition starting days before the opening)
 * fits with room to spare. A longer `from` / `to` is clamped, not refused.
 */
export const MEDAL_DAILY_CALENDAR_MAX_DAYS = 62;

// ─── Fields ──────────────────────────────────────────────────────────────────
//
// Strings trim FIRST, then check length: zod 3 runs checks in order, so
// `.min(1).trim()` would let "   " through and save it as "" (bug #5).

/** Lower-cased like `clientIdField`: "the same competitor twice" must be caught whatever case was sent. */
const idField = z.string().uuid().toLowerCase();
const reasonField = z.string().trim().min(1).max(MEDAL_REASON_MAX);
const medalField = z.enum(MEDALS);

// ─── PUT /events/:id/ranking ─────────────────────────────────────────────────

/**
 * One competitor's place in OVR's final ranking. An omitted key is `null`:
 * the call states the WHOLE ranking (see `eventRankingBody`). `.strict()`
 * because of that — a misspelt key would otherwise read as "no rank" and
 * clear what is stored.
 */
export const eventRankingEntryBody = z
	.object({
		competitor_id: idField,
		final_rank: z.number().int().min(1).max(EVENT_FINAL_RANK_MAX).nullable().optional(),
		proposed_medal: medalField.nullable().optional(),
	})
	.strict();

/**
 * `PUT /events/:id/ranking` — OVR's final ranking of the event, REPLACED
 * whole: listed competitors take the values sent, every other competitor of
 * the event goes back to no rank and no proposed medal. One set-based UPDATE;
 * the same call twice writes nothing.
 *
 * Refused: a competitor that is not entered in THIS event, or listed twice
 * (409 CONFLICT `{ field: "competitor_id", index, competitor_id }`); a locked
 * event (409 CONFLICT `{ locked: true }`). Ties are allowed (two `final_rank`
 * 3s); the medal desk decides what they mean.
 */
export const eventRankingBody = z
	.object({
		ranking: z.array(eventRankingEntryBody).min(1).max(EVENT_RANKING_MAX),
	})
	.strict();
export type EventRankingBody = z.input<typeof eventRankingBody>;

// ─── PUT /events/:id/medals ──────────────────────────────────────────────────

/**
 * One medal of the podium. `meet_competitor_id` is the result the award is
 * based on (optional): it must be a slot that THIS competitor holds in this
 * event. Omitted `meet_competitor_id` / `note` are `null` — the podium is
 * stated whole.
 */
export const eventPodiumEntryBody = z
	.object({
		competitor_id: idField,
		medal: medalField,
		meet_competitor_id: idField.nullable().optional(),
		note: z.string().trim().min(1).max(MEDAL_NOTE_MAX).nullable().optional(),
	})
	.strict();

/**
 * `PUT /events/:id/medals` — the medal desk sets the WHOLE podium in one
 * transaction: what is new is awarded, what changed is updated, what is no
 * longer listed is taken away. `[]` removes every medal.
 *
 * Every accepted call is also the desk's REVIEW of the event: it stamps
 * `medals_reviewed_at` / `_by` and moves `medals_version` on, so
 *  · sending the podium as it stands CONFIRMS it ("checked, it stands") —
 *    the event leaves `GET /medals/changed` until something moves again;
 *  · `[]` on an event with no medal DECIDES "no medals" (one entrant, every
 *    finalist disqualified) — it leaves `GET /medals/pending`. A reason is
 *    required for it.
 *
 * - `version` is the event's `medals_version` as the desk LOADED it
 *   (`GET /events/:id/medals` → `event.medals_version`); another value is 409
 *   `STALE_VERSION` and nothing is written — two desk screens cannot silently
 *   overwrite each other. A repeat of a call that went through is therefore
 *   stale too: load the podium again.
 * - `reason` is REQUIRED (422 `VALIDATION_FAILED { field: "reason" }`) when
 *   the call changes or removes a medal already awarded, when it decides
 *   "no medals" (`{ no_medals: true }`), when it awards a podium whose counts
 *   differ from the expected ones (`{ count_mismatch: PodiumCountMismatch[] }`,
 *   contracts' `podiumCountMismatches`), and for any change while the event
 *   is locked. The first award at the expected counts and a confirmation
 *   need none.
 * - Refused with 409 CONFLICT naming the field: the event is not a medal event
 *   (`{ field: "event_id", is_medal_event: false }`); a competitor listed
 *   twice, or not ENTERED in this event (`{ field: "competitor_id", index,
 *   competitor_id, status }`); a `meet_competitor_id` that is not a slot of
 *   that competitor in this event (`{ field: "meet_competitor_id", index, … }`).
 * - Counts that differ from the event's expected `gold_count` /
 *   `silver_count` / `bronze_count` (two golds and no silver after a tie, one
 *   bronze in a small field) are ACCEPTED with a reason: the desk decides,
 *   says why, and the response carries a warning.
 * - Allowed on a locked event: correcting medals is the desk's job.
 */
export const eventPodiumBody = z
	.object({
		podium: z.array(eventPodiumEntryBody).max(EVENT_PODIUM_MAX),
		version: versionField,
		/** The headline of this change in the audit trail. */
		reason: reasonField.optional(),
	})
	.strict();
export type EventPodiumBody = z.input<typeof eventPodiumBody>;

// ─── PUT /events/:id/medal-ceremony ──────────────────────────────────────────

/**
 * `PUT /events/:id/medal-ceremony` — when the event's medals are presented
 * (`events.medal_ceremony_at`; `null` clears it). The desk's, like the podium;
 * allowed on a locked event (it is not a result). Sending the stored value
 * writes nothing. The GMS programme editor (`PATCH /events/:id`, the setup
 * roles) still sets it too: one column, two doors, one audit trail.
 */
export const eventMedalCeremonyBody = z
	.object({
		medal_ceremony_at: z.string().trim().datetime({ offset: true }).nullable(),
		reason: reasonField.optional(),
	})
	.strict();
export type EventMedalCeremonyBody = z.input<typeof eventMedalCeremonyBody>;

/** `PUT /events/:id/medal-ceremony`. */
export interface EventMedalCeremony {
	event_id: string;
	medal_ceremony_at: string | null;
	/** False when the stored value was already the one sent: nothing was written. */
	changed: boolean;
}

// ─── POST /events/:id/lock, /unlock ──────────────────────────────────────────

/**
 * `POST /events/:id/lock` — from now on every write beneath the event (phases,
 * meets, competitors, start lists, results, line-ups, the ranking) is refused
 * with 409 CONFLICT `{ locked: true }`, until it is unlocked. Medals stay
 * editable. Locking a locked event changes nothing.
 */
export const eventLockBody = z.object({ reason: reasonField.optional() }).strict();
export type EventLockBody = z.input<typeof eventLockBody>;

/** `POST /events/:id/unlock` — `reason` is required. Unlocking an unlocked event changes nothing. */
export const eventUnlockBody = z.object({ reason: reasonField }).strict();
export type EventUnlockBody = z.input<typeof eventUnlockBody>;

// ─── Reads ───────────────────────────────────────────────────────────────────

/**
 * `GET /medals/tally?games_id=` — required: a tally is per games. Every other
 * key is optional and they combine with AND:
 *
 *  · `sport_id` narrows it to ONE sport: only medals of that sport's events
 *    count, and the rows are the organizations entered in that sport. A sport
 *    of another games (or none) answers an empty tally, not a 404.
 *  · `gender` — only that gender's events (`events.gender`).
 *  · `from` / `to` — games days (`competitionDayRangeShape`): only medals
 *    AWARDED on those days count (`awarded_at` in the games' time zone; a
 *    corrected colour is re-stamped, so it moves to the correction's day).
 *    The rows stay every organization entered, with zeros.
 *  · `breakdown` — every row also carries `cells` (one per sport, or per day
 *    a medal was awarded; empty cells left out) and the tally `columns`.
 *
 * One cached call serves the heatmap, the per-sport leaders, a contingent's
 * drawer and the by-day sparklines.
 */
export const medalTallyQuery = z
	.object({
		games_id: idField,
		sport_id: idField.optional(),
		gender: z.enum(EVENT_GENDERS).optional(),
		breakdown: z.enum(MEDAL_TALLY_BREAKDOWNS).optional(),
		...competitionDayRangeShape,
	})
	.superRefine(competitionFromBeforeTo);
export type MedalTallyQuery = z.input<typeof medalTallyQuery>;

/** `GET /medals/progress` — one games, every sport. */
export const medalProgressQuery = z.object({ games_id: idField });
export type MedalProgressQuery = z.input<typeof medalProgressQuery>;

/**
 * The medal list's keyset cursor: `<awarded_at as UTC text, microseconds>_<event_medal_id>`.
 * Opaque to clients — send back `next_cursor` exactly as received. The time is
 * Postgres' own full-precision text, never a JS `Date`: every medal of one
 * podium shares one `awarded_at`, and a millisecond cursor would skip some of
 * them at a page boundary (CLAUDE.md, bug 6).
 */
export const MEDAL_CURSOR_PATTERN =
	/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z)_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;
const strictUtcInstant = z.string().datetime();
const medalCursorField = z
	.string()
	.max(100)
	.regex(MEDAL_CURSOR_PATTERN, "Malformed cursor")
	.refine((v) => !v.startsWith("0000") && strictUtcInstant.safeParse(v.slice(0, v.lastIndexOf("_"))).success, "Malformed cursor");

/**
 * `GET /medals` — awarded medals, newest first: keyset on `(awarded_at,
 * event_medal_id)`, `Keyset<MedalListItem>`. Every filter is optional and they
 * combine with AND. Medals of an event that is not (or no longer) a medal
 * event are not listed, as they are not counted.
 */
export const listMedalsQuery = keysetQuery.extend({
	cursor: medalCursorField.optional(),
	games_id: idField.optional(),
	sport_id: idField.optional(),
	responsible_organization_id: idField.optional(),
});
export type ListMedalsQuery = z.input<typeof listMedalsQuery>;

/**
 * `GET /events/:id/medals/history` — the podium's audit trail, newest first:
 * keyset on `(occurred_at, audit_log_id)`, `Keyset<EventMedalHistoryItem>`.
 * The cursor has the medal list's shape (`<occurred_at as UTC text,
 * microseconds>_<audit_log_id>`): send back `next_cursor` as received.
 */
export const eventMedalHistoryQuery = keysetQuery.extend({
	cursor: medalCursorField.optional(),
});
export type EventMedalHistoryQuery = z.input<typeof eventMedalHistoryQuery>;

/**
 * `GET /medals/pending`, `/medals/changed` and `/medals/no-medals` — offset
 * pages, `Paginated<…>`. `q` matches the event's code or name (any part, any
 * case); `sport_id` keeps one sport; `order=sport` groups the list by sport
 * (programme order) and keeps the list's own order inside each.
 */
export const medalReviewQuery = paginationQuery.extend({
	games_id: idField.optional(),
	sport_id: idField.optional(),
	q: searchParam.optional(),
	order: z.enum(MEDAL_REVIEW_ORDERS).default("queue"),
});
export type MedalReviewQuery = z.input<typeof medalReviewQuery>;

/** `state=WAITING,AWARDED` — one or more medal event states, comma-separated. */
const medalEventStateList = z.string().max(200).transform(competitionSplitList).pipe(z.array(z.enum(MEDAL_EVENT_STATES)).min(1));

/**
 * `GET /medals/events` — every medal event of ONE games with its state
 * (`MedalEventList`). Offset pages, `page_size` ≤ 100: the set is bounded by
 * the programme (hundreds), every state must be computed before a state can
 * be filtered on anyway, and people want page numbers — the review lists'
 * reason. Filters combine with AND:
 *
 *  · `state` — one or more states; `state_counts` ignores it (a chip's count
 *    is what its list would show with the other filters kept);
 *  · `sport_id`; `responsible_organization_id` — events that organization
 *    has a competitor entered in; `q` — the event's code or name;
 *  · `from` / `to` — games days of the deciding meet (`due_at`).
 */
export const medalEventsQuery = paginationQuery
	.extend({
		games_id: idField,
		state: medalEventStateList.optional(),
		sport_id: idField.optional(),
		responsible_organization_id: idField.optional(),
		q: searchParam.optional(),
		sort: z.enum(MEDAL_EVENT_SORTS).default("schedule"),
		...competitionDayRangeShape,
	})
	.superRefine(competitionFromBeforeTo);
export type MedalEventsQuery = z.input<typeof medalEventsQuery>;

/**
 * `GET /medals/daily` — one games, per games day (`MedalDaily`). `from` / `to`
 * (games days) choose the calendar; omitted, it runs from the games' first
 * day (or the first day an event is decided, if earlier) to the last games,
 * deciding or award day — from the first award day only when the games has
 * no dates and nothing is scheduled. At most `MEDAL_DAILY_CALENDAR_MAX_DAYS`
 * days: a longer range is clamped.
 */
export const medalDailyQuery = z
	.object({
		games_id: idField,
		...competitionDayRangeShape,
	})
	.superRefine(competitionFromBeforeTo);
export type MedalDailyQuery = z.input<typeof medalDailyQuery>;

// ─── Responses ───────────────────────────────────────────────────────────────

/** A GMS login, as a name — who awarded a medal, who locked an event. */
export interface MedalUserRef {
	id: string;
	name: string;
}

/** A code and a name — the discipline and sport above an event, in the medal lists. */
export interface MedalCodeName {
	code: string;
	name: string;
}

export interface MedalEventRef {
	event_id: string;
	code: string;
	name: string;
}

/**
 * One competitor in OVR's proposal. Never a medal: `proposed_medal` is a
 * suggestion, and only `event_medals` rows count.
 */
export interface EventRankingItem {
	competitor: CompetitorRef;
	final_rank: number | null;
	proposed_medal: Medal | null;
}

/**
 * `PUT /events/:id/ranking` — every competitor of the event with the two
 * fields as stored, ranked first (`final_rank`, then `proposed_medal`), at
 * most `EVENT_RANKING_MAX` of them.
 */
export interface EventRankingResponse {
	event_id: string;
	competitors: EventRankingItem[];
}

/** The event block of `GET /events/:id/medals`. The three counts are what the event EXPECTS to award. */
export interface EventMedalEvent {
	event_id: string;
	code: string;
	name: string;
	is_medal_event: boolean;
	gold_count: number;
	silver_count: number;
	bronze_count: number;
	locked_at: string | null;
	medal_ceremony_at: string | null;
	/** Send it back with `PUT /events/:id/medals` (optimistic lock). Moves on every accepted podium call. */
	medals_version: number;
	/** The desk's last podium call — an award, a change, a confirmation or "no medals". */
	medals_reviewed_at: string | null;
	medals_reviewed_by: MedalUserRef | null;
}

/** One awarded medal. `members` is the roster: every member of a team receives the medal. */
export interface EventMedalItem {
	event_medal_id: string;
	medal: Medal;
	competitor: CompetitorRef;
	members: CompetitorMemberRef[];
	awarded_at: string;
	awarded_by: MedalUserRef;
	note: string | null;
	/** The result the award was based on — a slot this competitor holds in this event. */
	meet_competitor_id: string | null;
}

/** Expected against awarded, per colour — the numbers behind `warnings`, for a client that words its own. */
export interface MedalCountCheck {
	medal: Medal;
	expected: number;
	awarded: number;
}

/**
 * `GET /events/:id/medals`, and what `PUT /events/:id/medals` returns.
 *
 * `awarded` is ordered GOLD, SILVER, BRONZE. `proposal` is OVR's (at most
 * `MEDAL_PROPOSAL_MAX` rows, ranked first). `warnings` say in English where
 * the awarded counts differ from the expected ones ("Expected 1 silver, 0
 * awarded") — once anything is awarded; before that the podium is simply
 * not decided. `medal_counts` carries the same comparison as numbers.
 */
export interface EventMedals {
	event: EventMedalEvent;
	awarded: EventMedalItem[];
	proposal: EventRankingItem[];
	medal_counts: MedalCountCheck[];
	warnings: string[];
}

/** `POST /events/:id/lock` and `/unlock`. */
export interface EventLockState {
	event_id: string;
	code: string;
	locked_at: string | null;
	locked_by: MedalUserRef | null;
	/** False when the event was already in the asked-for state: nothing was written. */
	changed: boolean;
}

/** Medals per colour — a day's, a cell's, what an event expects. */
export interface MedalColourCounts {
	gold: number;
	silver: number;
	bronze: number;
}

/**
 * One cell of a tally breakdown: an organization's medals in ONE sport, or on
 * ONE games day. `key` is the column's (`MedalTallyColumn.key`). Only cells
 * with a medal are sent; a missing cell is zeros.
 */
export interface MedalTallyCell extends MedalColourCounts {
	key: string;
	total: number;
}

/**
 * A breakdown column. Sport: `key` = sport_id, `code` / `name` the sport's —
 * every sport of the games (narrowed by `sport_id`), programme order. Day:
 * `key` = `code` = `name` = the games day (`YYYY-MM-DD`) — every day a counted
 * medal was awarded, ascending.
 */
export interface MedalTallyColumn {
	key: string;
	code: string;
	name: string;
}

/** One organization in the tally. Organizations without a medal are listed with zeros. */
export interface MedalTallyRow {
	noc: NocRef;
	gold: number;
	silver: number;
	bronze: number;
	total: number;
	/** `rank()` over (gold, silver, bronze): ties share a rank — 1, 1, 3. */
	rank: number;
	/** With `breakdown`: this organization's cells, in column order. Absent otherwise. */
	cells?: MedalTallyCell[];
}

/**
 * `GET /medals/tally` — every organization with at least one competitor in
 * the games (with `sport_id`: in that sport), ordered gold, silver, bronze
 * (descending), then NOC code, then name. Grouped by ORGANIZATION, not
 * country: an organization may have none.
 */
export interface MedalTally {
	games_id: string;
	rows: MedalTallyRow[];
	/** Echoes the `breakdown` asked for; absent without one. */
	breakdown?: MedalTallyBreakdown;
	/** The breakdown's columns; absent without one. */
	columns?: MedalTallyColumn[];
}

/**
 * Where a games' medal events stand — the counts of `GET /medals/progress`.
 * Every MEDAL event of the programme (`is_medal_event`) is in exactly ONE of
 * the five states, so they add up to `events`:
 *
 *   awarded        it has a medal
 *   waiting        decided, not awarded: `GET /medals/pending`'s rule
 *                  (every medal meet OFFICIAL, no desk call since)
 *   no_medals      settled with no medal: `GET /medals/no-medals`'s rule
 *   competing      it has medal meets, not all of them OFFICIAL yet
 *   not_scheduled  no medal meet yet
 *
 * Medals: `medals_awarded` counts the awarded rows (a tie's second bronze
 * included — the tally's total); `medals_to_award` the expected
 * gold + silver + bronze of every event still to decide (waiting, competing,
 * not scheduled). An event settled with no medal owes none.
 */
export interface MedalProgressCounts {
	events: number;
	awarded: number;
	waiting: number;
	no_medals: number;
	competing: number;
	not_scheduled: number;
	medals_awarded: number;
	medals_to_award: number;
	/** Of `competing`: events with a medal meet LIVE now. */
	competing_live: number;
	/** Of `competing`: events with a medal meet UNOFFICIAL (finished, not yet official). */
	competing_unofficial: number;
}

/** One sport's counts; a sport with no medal event is listed with zeros. */
export interface MedalProgressSport extends MedalProgressCounts {
	sport_id: string;
	code: string;
	name: string;
}

/**
 * `GET /medals/progress` — the games' totals and one row per sport (the
 * games' sports, tens: bounded by the programme, so not paginated — the
 * tally's reason). ONE statement; sports in programme order (`sort_order`,
 * then name). Never cached: a meet made official in OVR moves `waiting`
 * without any medal write.
 */
export interface MedalProgress {
	games_id: string;
	totals: MedalProgressCounts;
	sports: MedalProgressSport[];
}

/** One row of `GET /medals`. */
export interface MedalListItem {
	event_medal_id: string;
	medal: Medal;
	awarded_at: string;
	event: MedalEventRef;
	discipline: MedalCodeName;
	sport: MedalCodeName;
	competitor: CompetitorRef;
}

/**
 * One row of `GET /medals/pending`: a medal event whose medal phases' top-level
 * meets are all OFFICIAL, that has no medal yet, and that the desk has not
 * decided "no medals" for since (`medals_reviewed_at` before `decided_at`).
 * `decided_at` is when the last of those meets became official. Oldest first.
 */
export interface MedalPendingItem {
	event: MedalEventRef;
	discipline: MedalCodeName;
	sport: MedalCodeName;
	decided_at: string | null;
	medal_ceremony_at: string | null;
	proposal: EventRankingItem[];
}

/**
 * One row of `GET /medals/no-medals`: a medal event with no medal that the
 * desk SETTLED that way — decided "no medals", or took every medal away —
 * and that is not back in the pending queue (no medal meet made official
 * since). Without this list such an event is on no screen at all: not
 * pending, not changed (nothing awarded), not in the medal list. Most recent
 * decision first.
 */
export interface MedalNoMedalsItem {
	event: MedalEventRef;
	discipline: MedalCodeName;
	sport: MedalCodeName;
	/** When the last medal meet became official; null while none is. */
	decided_at: string | null;
	/** The desk's call (`events.medals_reviewed_at`). */
	reviewed_at: string;
	reviewed_by: MedalUserRef | null;
	/** The reason the desk gave for that call — the audit headline; null when none was recorded. */
	reason: string | null;
	medal_ceremony_at: string | null;
}

/** A competitor whose awarded medal and OVR's proposed medal differ (either may be null). */
export interface MedalDifference {
	competitor_id: string;
	awarded: Medal | null;
	proposed: Medal | null;
}

/**
 * One row of `GET /medals/changed`: an awarded event the desk should look at
 * again because, since it last settled the podium (the later of the latest
 * award and its last review, `settled_at`):
 *
 *  · a result in a medal phase was saved (`results_changed_after_award`), or
 *  · OVR's proposal was written (`proposal_changed_after_award`) and differs
 *    from the podium (`differences`; empty when OVR proposed no medal).
 *
 * A podium the desk confirms (`PUT /events/:id/medals` with the same medals)
 * leaves the list until one of those moves again. Most recent activity first.
 */
export interface MedalChangedItem {
	event: MedalEventRef;
	discipline: MedalCodeName;
	sport: MedalCodeName;
	/** The latest `awarded_at` among the event's medals. */
	awarded_at: string;
	/** The desk's last podium call (`events.medals_reviewed_at`). */
	reviewed_at: string | null;
	/** The later of the two: what "since" means here. */
	settled_at: string;
	/** The latest result saved in a medal phase of the event. */
	result_updated_at: string | null;
	/** The latest write to the event's competitors — OVR's proposal is stored on them. */
	proposal_updated_at: string | null;
	results_changed_after_award: boolean;
	proposal_changed_after_award: boolean;
	differences: MedalDifference[];
}

// ─── The medal board ─────────────────────────────────────────────────────────

/** A sport, with its id — a medal board row links and filters by it. */
export interface MedalSportRef {
	sport_id: string;
	code: string;
	name: string;
}

/** A venue, by code and name — where the deciding meet is. */
export interface MedalVenueRef {
	venue_id: string;
	code: string;
	name: string;
}

/** How many events of the filtered set are in one state. */
export interface MedalStateCount {
	state: MedalEventState;
	count: number;
}

/** One awarded medal on a board row: who, which colour, when and by whom. */
export interface MedalMedallist {
	medal: Medal;
	competitor: CompetitorRef;
	awarded_at: string;
	awarded_by: MedalUserRef;
}

/**
 * One row of `GET /medals/events`: a medal event, where it stands and the
 * times the desk works to.
 *
 *  · `starts_at` / `due_at` — the first / last start among the event's medal
 *    meets (top level, not cancelled): `due_at` is when the medals are
 *    decided, `due_venue` where. Null while no medal meet is scheduled.
 *  · `decided_at` — when the last medal meet became OFFICIAL (null until every
 *    one is). The desk's target is `decided_at + MEDAL_AWARD_TARGET_MINUTES`.
 *  · `first_awarded_at` / `last_awarded_at` — the event's medals (a changed
 *    colour is re-stamped); `reviewed_at` / `reviewed_by` the desk's last call.
 *  · `medallists` — the podium, gold first. `[]` until awarded.
 */
export interface MedalEventItem {
	event: MedalEventRef;
	discipline: MedalCodeName;
	sport: MedalSportRef;
	gender: EventGender;
	state: MedalEventState;
	gold_count: number;
	silver_count: number;
	bronze_count: number;
	medal_meets: number;
	official_meets: number;
	live_meets: number;
	unofficial_meets: number;
	starts_at: string | null;
	due_at: string | null;
	due_venue: MedalVenueRef | null;
	decided_at: string | null;
	first_awarded_at: string | null;
	last_awarded_at: string | null;
	reviewed_at: string | null;
	reviewed_by: MedalUserRef | null;
	medal_ceremony_at: string | null;
	locked_at: string | null;
	medallists: MedalMedallist[];
}

/**
 * `GET /medals/events` — one offset page and, in the same statement,
 * `state_counts`: every state in `MEDAL_EVENT_STATES` order (zeros included),
 * counted over the same filters WITHOUT `state` — so a chip's count is exactly
 * the `total` its list shows. Never cached: OVR making a meet official moves
 * a state with no desk write.
 */
export interface MedalEventList extends Paginated<MedalEventItem> {
	state_counts: MedalStateCount[];
}

/** The first medal of a colour — of the games, or of one day. */
export interface MedalFirst {
	medal: Medal;
	awarded_at: string;
	event: MedalEventRef;
	sport: MedalSportRef;
	competitor: CompetitorRef;
}

/**
 * One games day of `GET /medals/daily`. Events are placed on the day their
 * deciding meet starts (`due_at` in the games' time zone); medals on the day
 * they were awarded (`awarded_at`, same zone).
 *
 *  · `events_due` — medal events due that day; `events_settled` — of those,
 *    awarded or settled with no medal; `medals_due` — what they expect.
 *  · `medals_awarded` — medals awarded that day, any event (a tie's second
 *    bronze included): the days add up to the tally's totals.
 *  · `firsts` — the day's first gold, silver and bronze (each at most once).
 */
export interface MedalDay {
	day: string;
	events_due: number;
	events_settled: number;
	medals_due: MedalColourCounts;
	medals_awarded: MedalColourCounts;
	firsts: MedalFirst[];
}

/**
 * `GET /medals/daily` — ONE statement, every reader of competition data. Not
 * paginated: one row per calendar day, at most `MEDAL_DAILY_CALENDAR_MAX_DAYS`
 * (the progress endpoint's reason). `today` is the server's games-local date,
 * so every desk screen agrees on it whatever the viewer's zone. `to` is
 * exclusive. `unscheduled` — medal events with no medal meet scheduled yet,
 * and the medals they expect: with the days, they add up to every medal event.
 * `firsts` — the games' first gold, silver and bronze.
 */
export interface MedalDaily {
	games_id: string;
	time_zone: string;
	starts_on: string | null;
	ends_on: string | null;
	today: string;
	from: string;
	to: string;
	days: MedalDay[];
	unscheduled: { events: number; medals_due: MedalColourCounts };
	firsts: MedalFirst[];
}

/** The tables a podium's history is read from: its medals, and the event row (review stamp, ceremony, lock). */
export const MEDAL_HISTORY_SOURCES = ["event_medals", "events"] as const;
export type MedalHistorySource = (typeof MEDAL_HISTORY_SOURCES)[number];

/**
 * The named values of one side (before / after) of a history row — nothing
 * else of the audited row leaves. A medal row fills `medal` / `note`; an event
 * row the three stamps. Timestamps are ISO text as the audit stored them.
 */
export interface EventMedalHistoryValues {
	medal: Medal | null;
	note: string | null;
	medal_ceremony_at: string | null;
	locked_at: string | null;
	medals_reviewed_at: string | null;
}

/**
 * One row of `GET /events/:id/medals/history`: an audited write to the event's
 * medals, or to its review stamp, ceremony time or lock. Rows of ONE desk call
 * share `request_id` (an award writes the medals and the stamp together), so
 * a client groups them into calls. `changed_fields` lists only the named
 * fields; `competitor` is the medal's (null on an event row).
 */
export interface EventMedalHistoryItem {
	audit_log_id: string;
	occurred_at: string;
	source: MedalHistorySource;
	action: "INSERT" | "UPDATE" | "DELETE";
	actor: MedalUserRef | null;
	reason: string | null;
	request_id: string | null;
	changed_fields: string[];
	competitor: CompetitorRef | null;
	before: EventMedalHistoryValues | null;
	after: EventMedalHistoryValues | null;
}
